import Button from '2-core-elements/Buttons/Button';
import RadioSelect from '5-react-form-apps/FormComponents/RadioSelect/RadioSelect';
import FormFieldWrapper from '6-my-mcc/FormElements/FormFieldWrapper/FormFieldWrapper';
import { useEffect, type FC } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type { CommunicationPreferencesFormType } from '../../Utils/useCommunicationPreferencesConfig';
import useRenewalMutation from '../../Utils/useRenewalNoticeMutation';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import useCommunicationPreferencesValue from '../../Utils/useCommunicationPreferencesValue';
import { pushGTMEvent } from 'utils/eventGTM';

type FormData = {
    [K in CommunicationPreferencesFormType['code']]: string;
}

interface FormRenewalNoticeProps {
    renewalNoticeConfig: CommunicationPreferencesFormType;
    value: string | null;
    onSubmitSuccess: () => void;
}

const FormRenewalNotice: FC<FormRenewalNoticeProps> = ({ renewalNoticeConfig, value: initialValue, onSubmitSuccess }) => {
    const methods = useForm<FormData>();
    const { control, handleSubmit } = methods;
    const submitFormMutation = useRenewalMutation();
    const { refetch: preferencesValueRefetch, isFetching: isPreferencesValueFetching } = useCommunicationPreferencesValue();

    useEffect(() => {
        pushGTMEvent({
            actionType: 'form_start',
            name: 'Renewal notice',
            type: 'Communication preferences',
        });
    }, []);

    const onSubmit = (data: FormData): void => {
        submitFormMutation.mutate(data[renewalNoticeConfig.code], {
            onSuccess: async() => {
                await preferencesValueRefetch();
                onSubmitSuccess();
            },
        });
    };

    return (
        <div className="mcc-form-wrapper">
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormFieldWrapper
                        id={renewalNoticeConfig.code}
                        label={renewalNoticeConfig.name}
                        frequency={renewalNoticeConfig.frequency}
                        description={renewalNoticeConfig.description}
                    >
                        <Controller
                            name={renewalNoticeConfig.code}
                            control={control}
                            defaultValue={typeof initialValue === 'string' ? initialValue : ''}
                            render={({ field }): JSX.Element => {
                                return (
                                    <RadioSelect
                                        value={field.value || ''}
                                        onChange={(value): void => {
                                            field.onChange(value);
                                        }}
                                    >
                                        <RadioSelect.Item
                                            value="HardCopy"
                                            label="Hard Copy"
                                        />
                                        <RadioSelect.Item
                                            value="Electronic"
                                            label="Electronic"
                                        />
                                    </RadioSelect>
                                );
                            }}
                        />
                    </FormFieldWrapper>

                    {submitFormMutation.isLoading || isPreferencesValueFetching ? (
                        <LoadingIndicator />
                    ) : (
                        <Button type="submit" fullWidth={true}>Save</Button>
                    )}
                </form>
            </FormProvider>
        </div>
    );
};

export default FormRenewalNotice;
